import { api } from "./api.js";
import { LIBRARY_CLIENT_CHANGED_EVENT } from "../library/client-event.js";

// Shared legacy-client context for control-panel-ported modules (Paul,
// 2026-07-15: "the client chooser should be in the left menu, at the
// top of the navigation and cover all modules inside control"). One
// selection, made in the app-shell Partner nav, consumed by every
// ported module surface — Broadcast today; future control-panel ports
// read the same context instead of growing their own pickers.
//
// Module-level singleton cache (the circulate-products.ts precedent).
// The client list comes from the general legacy-clients endpoint
// (enabled clientsV2 rows with ANY ported module on, per-module flags
// riding each row) — grown for the Attend port exactly as this file's
// original header promised: only THIS file changed.

export interface LegacyClient {
	id: number;
	keyword: string;
	name: string;
	/** Per-module enablement (module_enabled_* resolved server-side) —
	    e.g. { broadcast: true, attend: false }. Module pages still gate
	    on their own context endpoint; this only shapes the picker. */
	modules?: Record<string, boolean>;
}

const STORE_KEY = "cos.legacy.client";
// B1a stored the pick under a module-scoped key; migrate it silently.
const OLD_BROADCAST_KEY = "cos.broadcast.client";

// Window event fired on every pick — surfaces re-read legacyClientId().
export const LEGACY_CLIENT_EVENT = LIBRARY_CLIENT_CHANGED_EVENT;

let cache: Promise<LegacyClient[]> | null = null;

/** Test-only — clear the cache so vitest cases can observe an uncached
 * fetch regardless of what ran before them in the same file (the
 * module-level cache otherwise persists across every test in a run). */
export function _resetForTests(): void {
	cache = null;
}

export function loadLegacyClients(force = false): Promise<LegacyClient[]> {
	if (force || !cache) {
		cache = (api.getLegacyClients() as Promise<{ clients?: LegacyClient[] }>)
			.then((p) => p.clients ?? [])
			.catch((err: unknown) => {
				cache = null; // failures aren't cached (the circulate-products rule)
				throw err;
			});
	}
	return cache;
}

/**
 * Loads the caller's legacy clients and, if exactly one is available and
 * none is chosen yet, silently picks it (single-client staff never need to
 * choose). Used by app.ts's library-gate route guard, right before it
 * decides whether to gate a partner-side route: a staffer with only one
 * library available must never see a "choose a library" prompt that clears
 * itself a moment later. cos-app-shell.ts's own client-list load
 * (`_loadLegacyClients`) makes the identical auto-pick check inline rather
 * than calling this, so a test can mock loadLegacyClients() there directly.
 */
export async function ensureLegacyClientAutoPicked(): Promise<LegacyClient[]> {
	const clients = await loadLegacyClients();
	if (legacyClientId() === 0 && clients.length === 1) {
		pickLegacyClient(clients[0].id);
	}
	return clients;
}

export function legacyClientId(): number {
	try {
		const v = Number(sessionStorage.getItem(STORE_KEY) ?? "0");
		if (v > 0) return v;
		const old = Number(sessionStorage.getItem(OLD_BROADCAST_KEY) ?? "0");
		if (old > 0) {
			sessionStorage.setItem(STORE_KEY, String(old));
			return old;
		}
	} catch {
		/* sessionStorage unavailable — no sticky choice */
	}
	return 0;
}

/**
 * Forget the chosen library.
 *
 * Called on sign-out (Paul, 2026-07-29): the pick is remembered for the
 * SESSION so flipping between the Communico and Partner workspaces does
 * not re-ask, but signing out must not leave the next person looking at
 * a library they did not choose. sessionStorage would survive a
 * sign-out in the same tab, so this is explicit rather than implied.
 */
export function clearLegacyClient(): void {
	try {
		sessionStorage.removeItem(STORE_KEY);
		sessionStorage.removeItem(OLD_BROADCAST_KEY);
	} catch {
		/* nothing stored */
	}
	window.dispatchEvent(new CustomEvent(LEGACY_CLIENT_EVENT, { detail: { id: 0 } }));
}

export function pickLegacyClient(id: number): void {
	try {
		if (id > 0) sessionStorage.setItem(STORE_KEY, String(id));
		else sessionStorage.removeItem(STORE_KEY);
	} catch {
		/* not sticky, but the event still fires */
	}
	window.dispatchEvent(new CustomEvent(LEGACY_CLIENT_EVENT, { detail: { id } }));
}

// Measure drill audit (2026-08-14, defect #2): every "choose a library
// first" gate used to just SAY the picker lived "in the Client box above"
// (wrong location, and nothing to click even if it had been right) — the
// picker itself lives in the app shell's left-sidebar nav, across a shadow
// boundary from any page that wants to ask for it. `cos:open-create-partner`
// is the precedent for this shape: a page dispatches a plain window event,
// cos-app-shell listens and does the shell-level thing (expand the sidebar
// if it's collapsed, open the nav overlay on a narrow viewport, open +
// focus the picker) — the page never needs to know HOW the picker is shown,
// only that asking for it works.
export const OPEN_CLIENT_PICKER_EVENT = 'cos:open-client-picker';

export function requestOpenClientPicker(): void {
	window.dispatchEvent(new CustomEvent(OPEN_CLIENT_PICKER_EVENT));
}

// The re-test link's library switch (utils/retestTarget.ts / cos-app.ts) —
// carried in the URL, not in session state. Measured in a real browser
// (Chromium via Playwright): a same-origin tab opened via target="_blank"
// does NOT inherit the opener's sessionStorage. target="_blank" implies
// rel="noopener" in modern Chrome regardless of what rel actually says, and
// sessionStorage is cloned into a new browsing context only when that
// context keeps its opener — so writing the switch into the CURRENT tab's
// sessionStorage before it opens a new one does two things wrong at once:
// the new tab boots with no pick at all, AND the current tab's own library
// silently changes out from under whoever was using it, for no reason (it
// never needed switching). A one-time id in the URL is the only channel
// that reliably reaches the tab that actually needs it.
export const LIBRARY_URL_PARAM = "lib";

/** Cheap, synchronous pre-check — lets a caller (cos-app's connectedCallback)
 *  skip the session-and-fetch round trip on every ordinary boot, when there
 *  is plainly nothing to apply. */
export function hasPendingLibraryParam(): boolean {
	return new URLSearchParams(window.location.search).has(LIBRARY_URL_PARAM);
}

/**
 * Applies a one-time library switch carried in the URL (`?lib=<id>`), then
 * always strips the param via history.replaceState — matched or not, so it
 * never lingers in the address bar or ends up in a bookmark/shared link.
 *
 * The id is resolved against the CALLER's own permitted client list
 * (loadLegacyClients()) before it's applied; an id that isn't in that list
 * is ignored — never silently "switched to" on the strength of a URL alone.
 *
 * Assumes the caller has already confirmed a session exists (this hits the
 * same authed endpoint loadLegacyClients() always does) — see cos-app's
 * `_applyLibraryFromURL`, which awaits `_ensureSession()` first and, when
 * there isn't one yet, leaves the param untouched so the post-login
 * redirect capture (captureRedirectAfterLogin) still sees it and this runs
 * again, successfully, once signed in.
 */
export async function applyLibraryFromURL(): Promise<void> {
	const params = new URLSearchParams(window.location.search);
	const raw = params.get(LIBRARY_URL_PARAM);
	if (raw === null) return;

	try {
		const id = Number(raw);
		if (id > 0) {
			const clients = await loadLegacyClients();
			if (clients.some((c) => c.id === id)) pickLegacyClient(id);
		}
	} catch {
		/* best-effort — a failed lookup just means no switch, never a broken page */
	} finally {
		const url = new URL(window.location.href);
		url.searchParams.delete(LIBRARY_URL_PARAM);
		history.replaceState(null, "", url.pathname + url.search + url.hash);
	}
}
